import isPlainObject from 'lodash/isPlainObject';
import { TokenRefresher } from '@axiom/auth';
import { Subject } from 'rxjs';
import { RestEndpoint } from '@data-client/rest';
import type { Schema, IRestEndpoint, RestGenerics } from '@data-client/rest';

import { BlockerUtil } from '../components/app/Blocker/Blocker';
import { CookieUtil } from '../utils/cookie-util';
import { DesignSysEnvUtil } from '../utils/design-sys-env-util';

import { apiRequest } from './api-request';
import type { RequestProps } from './api.types';
import {
  EndpointCache,
  type FetchWatcher as LegacyFetchWatcher,
} from './endpoint-cache';
import { DataClientController } from './data-client-controller';

type AnyRestEndpoint = IRestEndpoint;

/**
 * Read an imperative Data Client fetch result with nested entity relations
 * resolved.
 *
 * `controller.fetch` denormalizes its RETURNED value against an EMPTY entity
 * table — see @data-client/core `Controller.fetch`:
 * `denormalize(endpoint.schema, input, {}, args)`. So nested entity relations
 * (e.g. a LawSchool's `tags: [TagEntity]`, resolved by id lookup) come back
 * EMPTY even though the response normalized into the store correctly. The
 * `useApi`/Suspense path denormalizes against the full store, which is why it
 * resolves relations and this imperative path did not.
 *
 * Fix: `fetch` to trigger the request + store commit, then read the
 * denormalized result back from the committed store via `getResponse`. For a
 * schema-less endpoint there is nothing to denormalize, so return the raw
 * fetch result unchanged.
 */
export async function resolveDenormalized<T>(
  endpoint: AnyRestEndpoint,
  ...args: readonly unknown[]
): Promise<T> {
  const controller = DataClientController.get();
  const params = args as Parameters<typeof endpoint>;
  const fetchResult = await controller.fetch(endpoint, ...params);
  if (!endpoint.schema) {
    return fetchResult as T;
  }
  const { data } = controller.getResponse(
    endpoint,
    ...params,
    controller.getState()
  );
  return data as T;
}

const isBrowserProcess = (
  process: unknown
): process is { browser: boolean } => {
  return !!(process && (process as { browser: boolean }).browser);
};

type readParams = {
  body?: RequestProps['body'];
  disableCookieRefresh?: boolean;
  encodeQuery?: RequestProps['encodeQuery'];
  endpoint: RequestProps['endpoint'];
  headers?: RequestProps['headers'];
  method: RequestProps['method'];
  query?: RequestProps['query'];
  /**
   * Optional Data Client schema for normalizing the response into the
   * entity store. When provided, the call routes through Data Client's
   * controller and the returned descriptor is the new entity-store
   * shape (`__endpoint` / `__args` / `toPromise`). When omitted, the
   * call routes through the legacy `EndpointCache` and the returned
   * descriptor is the original RxJS-backed FetchWatcher (`hasResolved`,
   * `getData`, `listen`, etc.). Pre-existing call sites stay unchanged.
   */
  schema?: Schema;
  signal?: RequestProps['signal'];
  useFetch?: RequestProps['useFetch'];
};

type writeParams = {
  body?: RequestProps['body'];
  disableBlocker?: boolean;
  disableCookieRefresh?: boolean;
  encodeQuery?: RequestProps['encodeQuery'];
  endpoint: RequestProps['endpoint'];
  headers?: RequestProps['headers'];
  method: RequestProps['method'];
  query?: RequestProps['query'];
  schema?: Schema;
  signal?: RequestProps['signal'];
  useFetch?: RequestProps['useFetch'];
};

/**
 * wipeStoreFetchers to only be used by tests
 */
export const wipeStoreFetchers = new Subject();
/**
 * end test note
 */

/**
 * New-path descriptor produced by `Api.read({..., schema })`. Carries
 * the Data Client endpoint + args that `useApi` / `useApiWithErrors`
 * feed into `controller.fetchIfStale` for Suspense reads. `toPromise`
 * is the imperative path for non-render callers; it goes through
 * `controller.fetch` (always-fire), matching the legacy `toPromise`
 * semantics.
 */
export type DataClientFetchWatcher<T = unknown> = {
  __endpoint: AnyRestEndpoint;
  __args: readonly unknown[];
  toPromise(): Promise<T>;
};

/**
 * `FetchWatcher` is a discriminated union of the legacy RxJS-backed
 * descriptor and the new Data Client descriptor. The discriminator is
 * the presence of `__endpoint`. Consumers (`useApi`, `useApiWithErrors`,
 * `AsyncConnector`) branch on it.
 */
export type FetchWatcher<T = unknown> =
  LegacyFetchWatcher<T> | DataClientFetchWatcher<T>;

export const isDataClientFetchWatcher = <T>(
  w: FetchWatcher<T>
): w is DataClientFetchWatcher<T> =>
  Object.prototype.hasOwnProperty.call(w, '__endpoint');

/**
 * Denormalize a previously-fetched endpoint's response against a SUPPLIED store
 * state — no refetch. Lets a caller re-read a cached list result whenever the
 * normalized store changes (e.g. after a write merges into the same entities),
 * which is how `AsyncConnector` reflects writes without re-hitting the network.
 */
export function denormalizeFromState<T>(
  descriptor: Pick<DataClientFetchWatcher, '__endpoint' | '__args'>,
  state: unknown
): T {
  const controller = DataClientController.get();
  const params = descriptor.__args as Parameters<typeof descriptor.__endpoint>;
  const { data } = controller.getResponse(
    descriptor.__endpoint,
    ...params,
    state as ReturnType<typeof controller.getState>
  );
  return data as T;
}

type EndpointKey = string;

const buildEndpointKey = (
  method: string,
  endpoint: string,
  bodyOrQueryKey: string
): EndpointKey => `${method}:${endpoint}:${bodyOrQueryKey}`;

const stableJson = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value !== 'object') {
    return typeof value === 'string' ? value : JSON.stringify(value);
  }
  try {
    return JSON.stringify(
      Object.keys(value)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (value as Record<string, unknown>)[key];
          return acc;
        }, {})
    );
  } catch {
    return '';
  }
};

// The endpoint cache is keyed by `${method}:${endpoint}:${discriminator}`,
// and the RestEndpoint instance memoizes its `body` / `query` at
// construction time (the `fetch` override closes over them). Two calls
// to the same endpoint with the same discriminator therefore share a
// single endpoint instance — and a single captured body. So the
// discriminator must include every input that varies between callers.
//
// Body+query covers GET (query only), POST reads (body filters), and
// every write (PATCH/PUT/DELETE/POST) where successive calls send
// different bodies to the same URL. Earlier behavior keyed PATCH on
// query alone, which collapsed every PATCH to /candidates/<id> onto
// one cached endpoint with the FIRST body baked in — silently
// re-sending stale data on subsequent edits.
const discriminator = (params: readParams | writeParams): string =>
  `${stableJson(params.body)}|${stableJson(params.query)}`;

// The endpoint path is the second `:`-delimited segment of a cache key
// (`${method}:${endpoint}:${discriminator}`). Method and endpoint never contain
// a `:` (concrete URLs only — route params are already interpolated), so the
// split is unambiguous even though the discriminator JSON may contain `:`.
const pathFromEndpointKey = (key: EndpointKey): string =>
  key.split(':')[1] ?? '';

// GET endpoint keys are registered here at module scope — shared across every
// `Api` instance — rather than only in the writing instance's
// `endpointInstances`. A mutation's `update` hook patches cached GET lists by
// key, and the read that owns a list may live on a DIFFERENT `Api` instance
// than the write: e.g. a legacy-Node write (clientApiBase) whose entity shares
// the cache with a generated read (envoy) at the same `/tags` path. Endpoint
// keys omit the domain, so a given method+path+args resolves to one canonical
// cache slot regardless of which instance created the read — keying this
// registry the same way is therefore unambiguous.
const registeredGetKeys = new Set<EndpointKey>();

type Pk = string | number;

// Normalized results from the generated/legacy clients are wrapped as
// `{ data: <pk | pk[]> }` (after Data Client normalizes an entity schema, the
// nested entities become their pks). Pull those pks out of a mutation's
// normalized result so they can be spliced into sibling list caches.
const extractResultPks = (result: unknown): Pk[] => {
  const payload =
    result && typeof result === 'object' && 'data' in result
      ? result.data
      : result;
  const values = Array.isArray(payload) ? payload : [payload];
  return values.filter(
    (value): value is Pk =>
      typeof value === 'string' || typeof value === 'number'
  );
};

// Splice pks into an existing normalized list result, preserving its wrapper
// shape (`{ data: pk[] }` or a bare `pk[]`) and de-duplicating. Unknown shapes
// are returned untouched so we never corrupt a cache we don't understand.
const appendPksToListResult = (existing: unknown, pks: Pk[]): unknown => {
  const mergeInto = (arr: Pk[]): Pk[] => {
    const merged = [...arr];
    pks.forEach(pk => {
      if (!merged.includes(pk)) {
        merged.push(pk);
      }
    });
    return merged;
  };

  if (
    existing &&
    typeof existing === 'object' &&
    'data' in existing &&
    Array.isArray(existing.data)
  ) {
    return {
      ...(existing as object),
      data: mergeInto((existing as { data: Pk[] }).data),
    };
  }
  return Array.isArray(existing) ? mergeInto(existing as Pk[]) : existing;
};

// Remove pks from an existing normalized list result, preserving its wrapper
// shape. Mirror of `appendPksToListResult`, used for DELETE.
const removePksFromListResult = (existing: unknown, pks: Pk[]): unknown => {
  const without = (arr: Pk[]): Pk[] => arr.filter(pk => !pks.includes(pk));

  if (
    existing &&
    typeof existing === 'object' &&
    'data' in existing &&
    Array.isArray(existing.data)
  ) {
    return {
      ...(existing as object),
      data: without((existing as { data: Pk[] }).data),
    };
  }
  return Array.isArray(existing) ? without(existing as Pk[]) : existing;
};

// Split a record-scoped path (`/tags/<id>`) into its collection path (`/tags`)
// and the trailing id. Returns null when there is no parent segment.
const splitRecordPath = (
  path: string
): { collectionPath: string; id: string } | null => {
  const lastSlash = path.lastIndexOf('/');
  if (lastSlash <= 0) {
    return null;
  }
  const id = path.slice(lastSlash + 1);
  return id ? { collectionPath: path.slice(0, lastSlash), id } : null;
};

export class Api {
  protected domain: string;

  protected cookieDomain?: string;

  protected cookieTokenRefresher: TokenRefresher | null = null;

  /**
   * Legacy URL-keyed store. Used by every read / write that does NOT
   * supply a `schema:`. Behavior is identical to the previous
   * implementation: `EndpointCache.watch(...)` returns an RxJS-backed
   * `FetchWatcher` with `hasResolved`, `getData`, `listen`, etc.
   */
  protected store: Record<string, EndpointCache<unknown>>;

  /**
   * Data Client `RestEndpoint` instances. Only populated when a caller
   * passes `schema:` — those calls participate in the normalized entity
   * cache and propagate via shared `static key` across endpoints.
   *
   * Keyed by `${method}:${endpoint}:${stable(body|query)}` so identical
   * requests share the same Data Client endpoint identity (and therefore
   * the same cache row).
   */
  private endpointInstances = new Map<EndpointKey, AnyRestEndpoint>();

  protected useFetch?: boolean;

  constructor({
    domain,
    cookieDomain,
    useFetch = false,
  }: {
    domain: string;
    cookieDomain?: string;
    useFetch?: boolean;
  }) {
    if (!domain) {
      throw new Error('You must provide a domain for the api to call');
    }
    this.domain = domain;
    this.cookieDomain = cookieDomain;
    this.useFetch = useFetch;
    this.store = {};

    if (isBrowserProcess(process)) {
      this.cookieTokenRefresher = new TokenRefresher({
        // "cookieDomain: ()=>string" prevents race condition
        cookieDomain: () => `${this.getCookieDomain()}`,
        cookieName: () => CookieUtil.getUserCookieName(),
      });
    }

    wipeStoreFetchers.subscribe({
      next: () => {
        Object.keys(this.store).forEach(
          storeKey => delete this.store[storeKey]
        );
        this.endpointInstances.clear();
      },
    });
  }

  getCookieDomain() {
    return this.cookieDomain || DesignSysEnvUtil.cookieDomain;
  }

  // Overloads: callers without a `schema:` always travel the legacy
  // EndpointCache path and get a watcher with `hasResolved` / `getData` /
  // `refetch` / `listen`. Narrowing the return here means existing
  // consumers (tests, AsyncConnector, refresh* helpers) keep their
  // precise types. Schema-wired callers see the union and must branch
  // via `isDataClientFetchWatcher`.
  read<ResponseType = void>(
    params: Omit<readParams, 'schema'> & {
      schema?: undefined;
    } & (ResponseType extends void ? 'Must provide ResponseType' : unknown)
  ): LegacyFetchWatcher<ResponseType>;

  read<ResponseType = void>(
    params: readParams & { schema: Schema } & (ResponseType extends void
        ? 'Must provide ResponseType'
        : unknown)
  ): FetchWatcher<ResponseType>;

  read<ResponseType = void>(
    params: readParams &
      (ResponseType extends void ? 'Must provide ResponseType' : readParams)
  ): FetchWatcher<ResponseType> {
    const resolved = params as readParams;

    // Legacy path: no schema → exactly the previous EndpointCache flow.
    // This keeps every un-migrated caller (login auth, portal, etc.)
    // working unchanged. The returned watcher has `hasResolved` /
    // `getData` / `listen` / `refetch`, which `useApiWithErrors` and
    // `AsyncConnector` detect and use.
    //
    // We also take this path when a `schema:` is present but no Data
    // Client controller is registered. That covers unit tests rendering
    // components in isolation without `<DataProvider>` /
    // `<DataClientProviderBridge>` — the legacy EndpointCache + MSW
    // path keeps producing the same observable a test expects. The
    // migration is staged endpoint-by-endpoint, so falling back when
    // the bridge isn't mounted lets schema-wired endpoints coexist
    // with un-migrated test setups.
    if (!resolved.schema || !DataClientController.has()) {
      return this.getStore<ResponseType>(resolved.endpoint).watch({
        body: resolved.body,
        cookieDomain: this.getCookieDomain(),
        cookieTokenRefresher: resolved.disableCookieRefresh
          ? this.cookieTokenRefresher
          : null,
        domain: this.domain,
        encodeQuery: resolved.encodeQuery,
        endpoint: resolved.endpoint,
        headers: resolved.headers,
        method: resolved.method,
        query: resolved.query,
        signal: resolved.signal,
        useFetch: resolved.useFetch ?? this.useFetch,
      });
    }

    // New path: schema provided → route through Data Client.
    const endpoint = this.getOrCreateRestEndpoint(resolved);
    const args: readonly unknown[] = [];
    return {
      __endpoint: endpoint,
      __args: args,
      async toPromise() {
        return resolveDenormalized<ResponseType>(endpoint, ...args);
      },
    };
  }

  async write<ResponseType = void>(
    params: writeParams &
      (ResponseType extends void ? 'Must provide ResponseType' : writeParams)
  ): Promise<ResponseType | Record<string, never>> {
    const resolved = params as writeParams;

    if (
      resolved.method === 'PATCH' &&
      resolved.body &&
      isPlainObject(resolved.body) &&
      Object.keys(resolved.body as Record<string, unknown>).length === 0
    ) {
      return {};
    }

    const blockerEvent = resolved.disableBlocker ? null : BlockerUtil.block();

    // Schema-wired call but no Data Client controller registered (the
    // common case in unit tests, which run components in isolation
    // without `<DataProvider>` / `<DataClientProviderBridge>`). Fall
    // through to the legacy `apiRequest` path so MSW-based mocks still
    // intercept the request and the call returns the mocked response.
    // Production always has the bridge mounted at the app root, so this
    // branch only fires in test environments.
    const useLegacy = !resolved.schema || !DataClientController.has();

    // Legacy path: no schema → straight `apiRequest`, no Data Client.
    if (useLegacy) {
      return apiRequest({
        body: resolved.body,
        cookieDomain: this.getCookieDomain(),
        cookieTokenRefresher: resolved.disableCookieRefresh
          ? this.cookieTokenRefresher
          : null,
        domain: this.domain,
        encodeQuery: resolved.encodeQuery,
        endpoint: resolved.endpoint,
        headers: resolved.headers,
        method: resolved.method,
        query: resolved.query,
        signal: resolved.signal,
        useFetch: resolved.useFetch ?? this.useFetch,
      })
        .then(async data => {
          if (blockerEvent) {
            BlockerUtil.unblock(blockerEvent);
          }
          return data as Promise<ResponseType | Record<string, never>>;
        })
        .catch(e => {
          if (blockerEvent) {
            BlockerUtil.unblock(blockerEvent);
          }
          throw e;
        });
    }

    // New path: schema provided → mutation response merges into the
    // normalized cache and propagates to other reads keyed by the same
    // entity.
    const endpoint = this.getOrCreateRestEndpoint(resolved);

    try {
      const data = await resolveDenormalized<
        ResponseType | Record<string, never>
      >(endpoint);
      if (blockerEvent) {
        BlockerUtil.unblock(blockerEvent);
      }
      return data;
    } catch (e) {
      if (blockerEvent) {
        BlockerUtil.unblock(blockerEvent);
      }
      throw e;
    }
  }

  /**
   * Invalidate hits both stores. Legacy URL-keyed entry (if any) is
   * invalidated via `EndpointCache.invalidate`; Data Client matches are
   * invalidated through the controller. Both paths are silent —
   * `bypassBlocker` is forced `true` so the global "Loading…" overlay
   * doesn't gate the whole page on a background revalidation.
   *
   * Background here: most callers of `invalidate` are `refresh*` helpers
   * fired from a `useEffect` after a route mounts, intended to refresh
   * stale cache entries while the user already sees content. The legacy
   * `EndpointCache.invalidate` would set `BlockerUtil.block()` and only
   * unblock once the refetch completes; in a React-18-concurrent tree
   * with `v7_startTransition` navigation, the resulting `setState(true)`
   * commits but the matching `setState(false)` ends up in a transition
   * lane that never commits while another part of the tree is suspended
   * — so the overlay sticks. Skipping the blocker side-steps the whole
   * race.
   */
  async invalidate(endpoint: string, bypassBlocker = true): Promise<unknown> {
    const prefix = `:${endpoint}:`;
    let invalidatedDataClient = false;

    if (this.endpointInstances.size > 0 && DataClientController.has()) {
      const controller = DataClientController.get();
      for (const [key, inst] of this.endpointInstances) {
        if (key.includes(prefix)) {
          void controller.invalidate(inst);
          invalidatedDataClient = true;
        }
      }
    }

    // One path per endpoint. A path can end up in BOTH stores — a schema read
    // evaluated before the controller was registered lands in the legacy store,
    // and every read after it lands in Data Client — and `EndpointCache
    // .invalidate` does not merely mark the entry stale, it re-issues the
    // request. Invalidating both therefore puts two identical requests on the
    // wire. Data Client owns the endpoint whenever it has an instance for it;
    // the legacy store is only authoritative for schema-less reads, which never
    // produce one.
    if (invalidatedDataClient) {
      return;
    }

    return (
      this.store[endpoint]?.invalidate(undefined, bypassBlocker) ||
      Promise.resolve()
    );
  }

  invalidateAll() {
    const legacy = Object.values(this.store).map(async store =>
      store.invalidate()
    );
    if (this.endpointInstances.size === 0 || !DataClientController.has()) {
      return legacy;
    }

    const controller = DataClientController.get();
    const dataClient = [...this.endpointInstances.values()].map(async e =>
      controller.invalidate(e)
    );
    return [...legacy, ...dataClient];
  }

  clear(endpoint: string) {
    this.store[endpoint]?.clear();

    if (this.endpointInstances.size === 0 || !DataClientController.has()) {
      return;
    }
    const controller = DataClientController.get();
    const prefix = `:${endpoint}:`;
    for (const [key, inst] of this.endpointInstances) {
      if (key.includes(prefix)) {
        void controller.invalidate(inst);
        this.endpointInstances.delete(key);
      }
    }
  }

  protected getStore<ResponseType>(url: string): EndpointCache<ResponseType> {
    if (!this.store[url]) {
      this.store[url] = new EndpointCache();
    }
    return this.store[url] as EndpointCache<ResponseType>;
  }

  private getOrCreateRestEndpoint(
    params: readParams | writeParams
  ): AnyRestEndpoint {
    const key = buildEndpointKey(
      params.method,
      params.endpoint,
      discriminator(params)
    );
    const existing = this.endpointInstances.get(key);
    if (existing) return existing;

    const { method } = params;
    const sideEffect = method !== 'GET' || undefined;
    const { disableCookieRefresh } = params;
    const bodyAtConstruction = params.body;
    const queryAtConstruction = params.query;
    const headersAtConstruction = params.headers;
    const encodeQueryAtConstruction = params.encodeQuery;
    const endpointPath = params.endpoint;
    const signalAtConstruction = params.signal;
    const useFetchAtConstruction = params.useFetch ?? this.useFetch;
    const getDomain = () => this.domain;
    const getCookieDomain = () => this.getCookieDomain();
    const getCookieTokenRefresher = () => this.cookieTokenRefresher;

    // Override `fetch` directly rather than `fetchResponse` /
    // `parseResponse`. RestEndpoint's default `fetch` calls
    // `fetchResponse` then checks `response.ok` *before* invoking
    // `parseResponse` — and `apiRequest` returns the parsed payload
    // (not a `Response`), so `.ok` is undefined and Data Client treats
    // the success as a rejection, surfacing the data itself as the
    // rejection reason. Bypassing the response-shape check keeps the
    // legacy Superagent / cookie-refresh / ApiError semantics intact
    // while still letting Data Client handle cache + normalization on
    // top.
    //
    // `key()` is overridden because Data Client's default keys reads
    // through `${method} ${url(args)}` — and `url` is built from
    // `urlPrefix + path`. With `path: '/'` (we do all real work in
    // `fetch`), every endpoint collapses to `GET /`, collides on a
    // single cache slot, and silently overwrites siblings'
    // normalization. The base `Endpoint` constructor does
    // `Object.assign(self, options)`, so an own-property `key`
    // shadows the prototype method.
    // `update` is Data Client's hook for letting a mutation patch OTHER cached
    // endpoint results in the same normalizing commit. We use it so a schema
    // write feeds its response straight into the relevant cached GET list — with
    // NO follow-up GET / refresh:
    //   • create (POST/PUT to a collection): splice the created entity's pk into
    //     the SAME-path GET list (entity normalization already handles the
    //     per-record merge that PATCH relies on).
    //   • delete (DELETE to `/collection/<id>`): drop `<id>` from the PARENT
    //     collection's GET list.
    // Same-path/parent-path scoping keeps a mutation out of unrelated/filtered
    // reads (`/tags/name/:n`), and a PATCH `/tags/:id` matches no list (its
    // record merge is enough). GET (read) endpoints get `undefined`.
    const buildListUpdaters = (
      affectedPath: string,
      apply: (listResult: unknown) => unknown
    ) => {
      const updaters: Record<string, (listResult: unknown) => unknown> = {};
      for (const existingKey of registeredGetKeys) {
        if (pathFromEndpointKey(existingKey) !== affectedPath) {
          continue;
        }
        updaters[existingKey] = apply;
      }
      return updaters;
    };

    const update =
      sideEffect === undefined
        ? undefined
        : (result: unknown) => {
            if (method === 'DELETE') {
              const record = splitRecordPath(endpointPath);
              return record
                ? buildListUpdaters(record.collectionPath, listResult =>
                    removePksFromListResult(listResult, [record.id])
                  )
                : {};
            }
            // Only a create (POST/PUT to a collection) appends to a same-path
            // list. A PATCH relies on entity record merge alone — and its path
            // (`/collection/:id`) can legitimately collide with a single-entity
            // GET at the same path (e.g. GET vs PATCH /candidates/:id), so
            // appending there would corrupt that read's result and stall the
            // write's denormalized resolve.
            if (method !== 'POST' && method !== 'PUT') {
              return {};
            }
            const pks = extractResultPks(result);
            return pks.length === 0
              ? {}
              : buildListUpdaters(endpointPath, listResult =>
                  appendPksToListResult(listResult, pks)
                );
          };

    // DELETE responses carry a status envelope, not the entity, so normalizing
    // them against the caller's entity schema would mint a pk-less junk record.
    // The caller still passes a schema to opt into this Data Client path; we
    // just don't feed it to the endpoint for deletes — `update` (above) does the
    // cache surgery from the URL id instead.
    const endpointSchema = method === 'DELETE' ? undefined : params.schema;

    // RestEndpoint is pinned to `RestGenerics` below: left to infer its generic
    // from these options, it derives `update`'s expected type from our
    // overridden `fetch` signature, which `update` doesn't satisfy.
    const restOptions = {
      path: '/',
      method,
      name: key,
      sideEffect,
      schema: endpointSchema,
      update,
      key: () => key,
      async fetch() {
        return apiRequest({
          domain: getDomain(),
          cookieDomain: getCookieDomain(),
          cookieTokenRefresher: disableCookieRefresh
            ? null
            : getCookieTokenRefresher(),
          encodeQuery: encodeQueryAtConstruction,
          endpoint: endpointPath,
          method,
          body: bodyAtConstruction,
          query: queryAtConstruction,
          headers: headersAtConstruction,
          signal: signalAtConstruction,
          useFetch: useFetchAtConstruction,
        });
      },
    };
    const rest = new RestEndpoint<RestGenerics>(restOptions);

    this.endpointInstances.set(key, rest);
    if (method === 'GET') {
      registeredGetKeys.add(key);
    }
    return rest;
  }
}
